import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useRouteMatch } from 'react-router-dom-v5';
import { colors } from 'spw-theme';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentScenario,
  selectScenarioListType,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import {
  currentFloor,
  currentProject,
} from 'spw-redux/modules/projects/selectors';
import { scenariosTitle } from 'spw-programming-projects/program-summary/single-page-summary/utils';
import { buildSearchParams } from 'spw-programming-projects/program-summary/utils';
import { Box, FormGroup } from '@mui/material';
import DownloadButtonWithOptions, {
  DownloadOption,
} from 'spw-components/collab/download-button-with-options';
import { DownloadOptionsHeader } from 'spw-projects/components/program-summary-styles';
import {
  ProgramSummaryExportableType,
  PNG,
  ProgrammingReportAttributes,
} from 'spw-redux/modules/reports/types';
import { ProgramSummaryExportableTypes } from 'spw-redux/modules/reports/constants';
import { IProject } from 'spw-redux/modules/projects/types';

import { useLocation } from 'react-router';
import { Switch, SwitchFormControlLabel } from 'spw-components/global/styles';
import { RSFLabel } from 'spw-programming-projects/program-summary/program-summary-tabs/styles';
import { REPORT_TYPES } from 'spw-programming-projects/constants';
import { prepareAndExecuteProgrammingReportDownload } from 'spw-redux/modules/downloads/actions';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import { isMacro } from 'spw-components/worksheet/utils';
import { INDIVIDUAL_WORKSPACES_CATEGORY } from 'spw-constants';
import { selectIsDownloadingIndividualReport } from 'spw-redux/modules/downloads/selectors';
import useUnitConversion from 'spw-components/unit-conversion/hooks';

const Container = styled.div`
  display: flex;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  height: 36px;
  position: sticky;
  top: 52px;
  z-index: 1;
  background-color: white;
  margin-top: -8px;
  padding-top: 8px;
  padding-bottom: 10px;
`;

const ScenarioPath = styled.div`
  padding-left: 10px;
  display: flex;
  font-weight: normal;
  font-size: 18px;
`;

const Arrow = styled.div`
  display: flex;
  align-items: center;

  && {
    margin: 0 14px;

    img {
      margin-top: 4px;
    }
  }
`;

const LinkToScenariosList = styled(Link)`
  && {
    color: ${colors.greyInputAddornment};
    cursor: pointer;
    display: flex;
    align-items: center;

    p {
      margin: 0;
    }
  }
`;

const ScenarioName = styled.div`
  && {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 18px;
    color: ${colors.black};
  }
`;

const DownloadButtonContainer = styled.div`
  && {
    display: flex;
    flex-direction: row;
    margin-left: 50px;
  }
`;

enum DownloadOptions {
  OnePager = 'One Page Report',
  AllCardsIndividually = 'All Cards Individually',
  SummaryData = 'Summary Data',
}

type ProgramSummaryDownloadOption = DownloadOption & {
  exportableType: ProgramSummaryExportableType;
  isOpen?: boolean;
};

type DownloadRequestOptions = {
  downloadOption?: ProgramSummaryDownloadOption;
  downloadName?: string;
};

const DOWNLOAD_OPTIONS: ProgramSummaryDownloadOption[] = [
  {
    label: DownloadOptions.OnePager,
    format: PNG,
    exportableType: ProgramSummaryExportableTypes.ONE_PAGER,
  },
  {
    label: DownloadOptions.AllCardsIndividually,
    format: PNG,
    exportableType: ProgramSummaryExportableTypes.ALL_CARDS_INDIVIDUALLY,
  },
  {
    label: DownloadOptions.SummaryData,
    format: PNG,
    exportableType: ProgramSummaryExportableTypes.SUMMARY_DATA,
  },
];

type TopNavigationProps = {
  clientFacingNamesAreVisible: boolean;
  onToggleClientFacingNames: () => void;
  showRsf: boolean;
  onToggleRsf?: () => void;
};

const TopNavigation = ({
  clientFacingNamesAreVisible,
  onToggleClientFacingNames,
  showRsf,
  onToggleRsf,
}: TopNavigationProps) => {
  const { getUnit } = useUnitConversion();
  const dispatch = useDispatch();
  const project: IProject = useSelector(currentProject);
  const scenario = useSelector(selectCurrentScenario);
  const worksheet = useSelector(selectWorksheet);
  const [initialDownloadName, setInitialDownloadName] = useState('');

  const scenarioListType = useSelector(selectScenarioListType);
  const floor = useSelector(currentFloor);
  const title = scenariosTitle(scenarioListType, floor);
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);

  const searchParams = buildSearchParams(
    scenarioListType,
    urlSearchParams,
    floor
  );

  const individualReportIsDownloading = useSelector(
    selectIsDownloadingIndividualReport
  );

  const { url } = useRouteMatch();

  const downloadOptions = useMemo(() => {
    const optionsList: ProgramSummaryDownloadOption[] = [];

    DOWNLOAD_OPTIONS.forEach((option) => {
      optionsList.push({
        ...option,
        onClick: () => {
          const optionName =
            option.label === DownloadOptions.AllCardsIndividually
              ? 'All Cards'
              : option.label;
          setInitialDownloadName(`${project.projectName}_${optionName}`);
        },
      });
    });

    const spaceTypeCategories = project?.spaceTypeCategories.filter(
      (spaceTypeCategory) => {
        const { category } = spaceTypeCategory.defaultSpaceTypeCategory;
        if (isMacro(scenario) && scenario.isShared) {
          return (
            category === INDIVIDUAL_WORKSPACES_CATEGORY &&
            worksheet.rowsByCategory[category].length > 0
          );
        }
        return worksheet.rowsByCategory[category].length > 0;
      }
    );

    spaceTypeCategories.forEach((spaceTypeCategory) => {
      const option: ProgramSummaryDownloadOption = {
        label: spaceTypeCategory.name,
        onClick: () => {
          setInitialDownloadName(
            `${project.projectName}_${spaceTypeCategory.name}`
          );
        },
        format: PNG,
        exportableType: `${spaceTypeCategory.defaultSpaceTypeCategory.category}Cards` as ProgramSummaryExportableType,
      };

      optionsList.push(option);
    });

    return optionsList;
  }, [
    project?.projectName,
    project?.spaceTypeCategories,
    scenario,
    worksheet?.rowsByCategory,
  ]);

  const exportProgramSummary = ({
    downloadName,
    downloadOption,
  }: DownloadRequestOptions) => {
    dispatch(
      prepareAndExecuteProgrammingReportDownload({
        reportAttributes: {
          reportType: REPORT_TYPES.PROGRAM_SUMMARY,
          entityType: isTotalProgram(scenario) ? 'TOTAL_PROGRAM' : 'SCENARIO',
          entityId: scenario.id,
          format: downloadOption.format,
          exportableType: downloadOption.exportableType,
          shouldShowClientFacingNames: clientFacingNamesAreVisible,
          showRsf,
          isOpen: downloadOption.isOpen,
          worksheet,
          scenario,
        } as Partial<ProgrammingReportAttributes>,
        outputFileName: `${downloadName}.zip`,
      })
    );
  };

  return (
    <Container>
      <ScenarioPath>
        <LinkToScenariosList
          to={`${url.replace(/(scenarios)\/.*$/g, `$1?${searchParams}`)}`}
        >
          <p>{title}</p>
        </LinkToScenariosList>
        <Arrow>
          <img alt="arrow-back" src="/images/arrow-back.svg" />
        </Arrow>

        <ScenarioName>{scenario.name}</ScenarioName>
      </ScenarioPath>
      <Box marginLeft="auto" display="flex" flexDirection="row">
        <FormGroup aria-label="client facing names switch" row>
          <SwitchFormControlLabel
            control={
              <Switch
                checked={clientFacingNamesAreVisible}
                onChange={onToggleClientFacingNames}
                color="default"
              />
            }
            label={<RSFLabel>CLIENT NAMES</RSFLabel>}
            labelPlacement="start"
          />
        </FormGroup>

        <FormGroup row>
          <SwitchFormControlLabel
            control={
              <Switch
                checked={showRsf}
                onChange={onToggleRsf}
                color="default"
              />
            }
            label={<RSFLabel>{getUnit('rentable-area')}</RSFLabel>}
            labelPlacement="start"
          />
        </FormGroup>
      </Box>
      <DownloadButtonContainer>
        <DownloadButtonWithOptions
          initialDownloadName={initialDownloadName}
          maxDownloadNameLength={40}
          errorMessage="A name is required"
          isBusy={individualReportIsDownloading}
          disabled={false}
          options={downloadOptions}
          onSubmit={exportProgramSummary}
          popoverContainerStyle={{
            width: '100%',
            paddingBottom: '8px',
          }}
          header={
            <DownloadOptionsHeader>DOWNLOAD OPTIONS</DownloadOptionsHeader>
          }
        />
      </DownloadButtonContainer>
    </Container>
  );
};

export default TopNavigation;
